import React, { useMemo, useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import parse, { domToReact } from "html-react-parser";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { alert_error, alert_success } from "../../AppContext.js";
import { MoonLoader } from "react-spinners";
import { Cookies } from "../../index.js";
import PostsCodeBlock from "./PostsCodeBlock.jsx";

import Language_i18 from "../../js/useNationalLanguage.jsx";
import { isLocalHost } from "../../shared/host_utils.js";

const time_update = 2 * 24 * 60 * 60 * 1000; // 2 ngày in ms

const getDomNodeText = (node) => {
  if (!node) return "";
  if (node.type === "text") return node.data || "";
  if (node.name === "br") return "\n";
  if (!Array.isArray(node.children) || node.children.length === 0) return "";
  return node.children.map(getDomNodeText).join("");
};

const normalizeLeadingArticleHtml = (html) => {
  if (typeof html !== "string") return html;

  let normalized = html.replace(/^\uFEFF+/, "").trimStart();

  const leadingEmptyTagPattern =
    /^(?:<(?:br)\s*\/?>|<(?:(?:p|div|span))\b[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/(?:p|div|span)>)+/i;

  while (leadingEmptyTagPattern.test(normalized)) {
    normalized = normalized.replace(leadingEmptyTagPattern, "").trimStart();
  }

  return normalized;
};

const Posts = ({ uri, show_start_load, embedded = false, showHelpfulBox = false }) => {
  const { i18n, lang } = Language_i18();
  const { slug_2 } = useParams();
  const content_code = uri || slug_2;
  const [content, setContent] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState("");
  const [helpfulVote, setHelpfulVote] = useState("");
  const [isLiked, setIsLiked] = useState(false);
  const national_market = Cookies.get("national_market") || "vi";
  const latest_version = useSelector((state) => state.latest_version);

  const getArticleUriTail = () => {
    try {
      const path = String(window.location.pathname || "");
      const segments = path.split("/").filter(Boolean);
      const tail = segments[segments.length - 1];
      return tail || String(content_code || "").trim();
    } catch {
      return String(content_code || "").trim();
    }
  };

  const getLikeStorageKey = () => {
    const tail = getArticleUriTail();
    return `liked_article_${tail}`;
  };

  // Định nghĩa nhãn đa ngôn ngữ
  const labels = {
    en: {
      share: "Share",
      writtenDate: "Written date:",
      apiError: "API error occurred, please contact admin",
      unknownError: "An unknown error occurred, please contact admin",
    },
    vi: {
      share: "Chia sẻ",
      writtenDate: "Ngày viết:",
      apiError: "Đã xảy ra lỗi API, vui lòng liên hệ admin",
      unknownError: "Đã xảy ra lỗi không xác định, vui lòng liên hệ admin",
    },
  };

  // 1. Hàm dịch nội dung (trả về html đã dịch hoặc nội dung gốc nếu không cần dịch/ lỗi)
  function cleanupTranslatedHtml(html) {
    if (typeof html !== "string") return html;
    return html
      .replace(/(<div[^>]*support-grid[^>]*>)\s*Left\s*(<div)/gi, "$1$2")
      .replace(/(<\/div>)\s*Right\.?\s*(<div)/gi, "$1$2");
  }

  async function translateContent(rawContent, national_market) {
    // Skip translation for Vietnamese market and for non-latest versions.
    if (national_market === "vi" || latest_version != 1) return rawContent;

    try {
      const formData = new FormData();
      formData.append("content", rawContent);
      const res = await axios.post(
        "http://vip.tecom.pro:8789/translate?category=html",
        formData
      );
      return res?.data?.status === "success"
        ? cleanupTranslatedHtml(res?.data?.translate) || rawContent
        : rawContent;
    } catch (err) {
      console.error("Translation API error:", err);
      return rawContent;
    }
  }

  // 2. Hàm lưu cache vào localStorage
  function saveCache(key, dataObj) {
    try {
      localStorage.setItem(key, JSON.stringify(dataObj));
    } catch (e) {
      console.warn("Không lưu được cache cuối cùng:", e);
    }
  }

  function savePostsTypeCache(postsType) {
    const value = String(postsType || "");
    try {
      localStorage.setItem("posts_type", value);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("posts-type-change", { detail: value })
        );
      }
    } catch (e) {
      console.warn("Không lưu được posts_type:", e);
    }
  }

  function resetPostsTypeCache() {
    try {
      localStorage.setItem("posts_type", "");
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("posts-type-change", { detail: "" })
        );
      }
    } catch (e) {
      console.warn("Không reset được posts_type:", e);
    }
  }

  function saveRelatedPostsCache(relatedPosts) {
    const normalized = Array.isArray(relatedPosts) ? relatedPosts : [];
    try {
      localStorage.setItem("related_posts", JSON.stringify(normalized));
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("posts-related-change", { detail: normalized })
        );
      }
    } catch (e) {
      console.warn("Không lưu được related_posts:", e);
    }
  }

  function resetRelatedPostsCache() {
    try {
      localStorage.setItem("related_posts", JSON.stringify([]));
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("posts-related-change", { detail: [] })
        );
      }
    } catch (e) {
      console.warn("Không reset được related_posts:", e);
    }
  }

  // 3. Hàm render nội dung: dịch, cập nhật record, setContent và lưu cache
  const renderContent = async (data) => {
    const record =
      typeof data === "string" ? JSON.parse(data) : JSON.parse(JSON.stringify(data));

    const rawHtml = record.content;
    const needsTranslation = national_market !== "vi";

    const buildRecord = (html) => {
      const next = {
        ...record,
        content: html,
        national_market,
        timestamp: Date.now(),
        originalContent: rawHtml,
      };
      if (next.info?.data) {
        next.info.data.content = html;
      }
      return next;
    };

    if (needsTranslation) {
      setIsTranslating(true);
      setContent(buildRecord(rawHtml));
      const translatedHtml = await translateContent(rawHtml, national_market);
      const translatedRecord = buildRecord(translatedHtml);
      setContent(translatedRecord);
      saveCache(`contents_${content_code}`, translatedRecord);
      setIsTranslating(false);
    } else {
      const finalRecord = buildRecord(rawHtml);
      setContent(finalRecord);
      saveCache(`contents_${content_code}`, finalRecord);
      setIsTranslating(false);
    }
  };

  const fetchData = async (code) => {
    if (!code) return;
    try {
      const response = await axios.get(
        `https://hust.media/api/content/getdata_v1.php?uri=${code}&mode=posts`
      );
      processInfo(response.data);
    } catch (err) {
      alert_error(labels[lang]?.apiError || labels.en.apiError);
      setError(labels[lang]?.apiError || labels.en.apiError);
    }
  };

  const processInfo = (info) => {
    const { status, message, data, related_posts, redirect } = info;
    if (status === 1) {
      const mergedData = {
        ...(data || {}),
        related_posts: Array.isArray(related_posts) ? related_posts : [],
      };
      savePostsTypeCache(mergedData?.posts_type);
      saveRelatedPostsCache(mergedData?.related_posts);
      if (show_start_load !== "NO" && latest_version != 3) {
        alert_success(message);
      }
      renderContent(mergedData);
    } else if (status === 0) {
      alert_error(message);
      setError(message);
    } else {
      alert_error(labels[lang]?.unknownError || labels.en.unknownError);
      setError(labels[lang]?.unknownError || labels.en.unknownError);
    }
    if (redirect) setTimeout(() => { window.location.href = redirect; }, 1700);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");

    if (!content_code) return;
    resetPostsTypeCache();
    resetRelatedPostsCache();
    if (isLocalHost(window.location.hostname) || mode == "auto_reload") {
      fetchData(content_code);
      return;
    }
    const raw = localStorage.getItem(`contents_${content_code}`);
    if (raw) {
      try {
        const record = JSON.parse(raw);
        const { timestamp, national_market: savedMarket } = record;
        const isExpired = Date.now() - timestamp > time_update;
        const marketChanged = savedMarket !== national_market;
        if (!isExpired && !marketChanged) {
          setContent(record);
          savePostsTypeCache(record?.posts_type);
          saveRelatedPostsCache(record?.related_posts);
          return;
        } else {
          console.log("reset cached");
        }
      } catch (e) {
        console.warn("Không đọc được cache:", e);
      }
    } else {
      console.log("reset cached", content_code);
    }
    fetchData(content_code);
  }, [uri, slug_2, national_market]);

  useEffect(() => {
    const key = getLikeStorageKey();
    try {
      const saved = localStorage.getItem(key);
      setIsLiked(saved === "true");
    } catch {
      setIsLiked(false);
    }
  }, [uri, slug_2]);

  const handleShareButtonClick = async () => {
    try {
      await navigator.share({
        title: content.title,
        text: content.description,
        url: window.location.href,
      });
    } catch (err) {
      console.error("Share error:", err);
    }
  };

  const handleHelpfulVote = (vote) => {
    setHelpfulVote(vote === "yes" ? "yes" : "no");
  };

  const handleToggleLike = () => {
    const key = getLikeStorageKey();
    setIsLiked((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(key, String(next));
      } catch {
        // no-op
      }
      return next;
    });
  };

  // ===== UI-only helpers (không đổi logic) =====
  const formatUsDateTime = (value) => {
    const raw = String(value || "").trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
    if (match) {
      const [, yyyy, mm, dd, hh, mi, ss] = match;
      return `${mm}/${dd}/${yyyy} ${hh}:${mi}:${ss}`;
    }
    return raw;
  };
  const safeTitle = content?.title || "";
  const safeDesc = content?.description || "";
  const safeDate = formatUsDateTime(content?.createdate || "");
  const safeHashName = (content?.tips_hash_name || "").toString().trim() || "Hust Media";
  const htmlToRender =
    isTranslating && content?.originalContent
      ? content.originalContent
      : content?.content || content;
  const normalizedHtmlToRender = useMemo(
    () => normalizeLeadingArticleHtml(htmlToRender),
    [htmlToRender]
  );
  const renderedArticle = useMemo(() => {
    if (typeof normalizedHtmlToRender !== "string") return normalizedHtmlToRender;

    return parse(normalizedHtmlToRender, {
      replace(domNode) {
        if (
          domNode?.type === "tag" &&
          domNode?.name === "div" &&
          domNode?.attribs?.["data-code-block"] !== undefined
        ) {
          const language = domNode.attribs["data-language"] || "text";
          const code = getDomNodeText(domNode).replace(/^\n+|\n+$/g, "");
          return <PostsCodeBlock language={language} code={code} />;
        }

        if (
          domNode?.type === "tag" &&
          domNode?.name === "code" &&
          domNode?.parent?.name === "pre"
        ) {
          const currentClass = String(domNode.attribs?.class || "").trim();
          return (
            <code className={currentClass ? `${currentClass} font-mono` : "font-mono"}>
              {domToReact(domNode.children || [])}
            </code>
          );
        }

        return undefined;
      },
    });
  }, [normalizedHtmlToRender]);

  return (
    <>
      {(!content && !error && show_start_load !== "NO") ? (
        <div className={`${embedded ? "min-h-[40vh] px-0 py-0 max-lg:mt-2 lg:mt-6" : "min-h-[40vh] px-3 py-10"} bg-[radial-gradient(1200px_600px_at_50%_-150px,rgba(236,72,153,.12),transparent_60%),radial-gradient(900px_500px_at_10%_10%,rgba(59,130,246,.10),transparent_55%),radial-gradient(900px_500px_at_90%_20%,rgba(34,197,94,.08),transparent_55%)]`}>
          <div className={embedded ? "mx-0 max-w-none" : "mx-auto max-w-4xl"}>
            <div className="rounded-3xl border border-slate-200/70 bg-white/80 p-6 shadow-xl ring-1 ring-black/5 backdrop-blur-md">
              <div className="flex flex-col items-center justify-center gap-3 py-10">
                <MoonLoader size={64} />
                <div className="text-sm text-slate-500">Loading articles...</div>
              </div>
            </div>
          </div>
        </div>
      ) : content ? (
        <>
          <Helmet>
            <title>{safeTitle}</title>
            <meta name="description" content={safeDesc} />
            <meta name="author" content={safeTitle} />
            <meta property="og:title" content={safeTitle} />
            <meta property="og:site_name" content="Bài viết Hust Media" />
            <meta property="og:url" content="https://hust.media" />
            <meta property="og:description" content={safeDesc} />
          </Helmet>

          <div className={`${embedded ? "px-0 py-0 sm:py-0 max-lg:mt-2 lg:mt-6" : "px-3 py-6 sm:py-10"} bg-[radial-gradient(1200px_600px_at_50%_-150px,rgba(236,72,153,.12),transparent_60%),radial-gradient(900px_500px_at_10%_10%,rgba(59,130,246,.10),transparent_55%),radial-gradient(900px_500px_at_90%_20%,rgba(34,197,94,.08),transparent_55%)]`}>
            <div className={embedded ? "mx-0 max-w-none" : "mx-auto max-w-4xl"}>
              <div className="rounded-3xl border border-slate-200/70 bg-white/85 shadow-2xl ring-1 ring-black/5 backdrop-blur-md">
                {/* Header */}
                <div className="max-lg:px-3 lg:px-7 pb-2 max-lg:pt-7 lg:pt-12">
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="min-w-0 flex-1 text-balance text-xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                        {safeTitle}
                      </h2>

                      {/* Back link */}
                      <Link
                        to="/shop/category/tips_vip"
                        className="inline-flex shrink-0 items-center no-underline rounded-2xl border border-pink-200 bg-pink-50 px-3 py-1.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-pink-100 hover:no-underline active:scale-[0.98]"
                      >
                        Back to Insights
                      </Link>
                    </div>

                      {safeDesc ? (
                        <div className="mt-2 line-clamp-3 text-pretty text-sm leading-relaxed text-slate-600 sm:text-base">
                          {safeDesc}
                        </div>
                      ) : null}

                      <div className="mt-3 flex min-w-0 flex-row flex-nowrap items-center gap-2 text-xs text-slate-500 sm:flex-wrap sm:gap-x-3 sm:gap-y-2 sm:text-sm">
                        <span className="inline-flex min-w-0 flex-1 items-start gap-2 rounded-full bg-slate-100 px-3 py-1 sm:flex-none sm:w-auto">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            className="text-slate-500"
                            aria-hidden="true"
                          >
                            <path
                              d="M7 3v2M17 3v2M4 8h16M6 12h4m-4 4h6m9-8v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="min-w-0 whitespace-normal break-words leading-tight">
                            {labels[lang]?.writtenDate}{" "}
                            <span className="font-medium text-slate-700">{safeDate}</span>
                          </span>
                        </span>

                        {safeHashName ? (
                          <span className="inline-flex max-w-[44%] shrink-0 items-center whitespace-nowrap rounded-full border border-[#D8E0E8] bg-[#EEF2F6] px-2.5 py-1 font-semibold text-[#5E6B7A] sm:max-w-none">
                            {safeHashName}
                          </span>
                        ) : null}

                        {isTranslating ? (
                          <span className="text-slate-500">Đang dịch nội dung…</span>
                        ) : null}
                      </div>
                  </div>
                </div>

                {/* Translating bar */}
                {isTranslating && (
                  <div className="max-lg:px-2 lg:px-7">
                    <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-200">
                      <div className="loading-bar-animation absolute left-0 top-0 h-full w-1/2 bg-sky-500" />
                    </div>
                    <style>{`
                      @keyframes loadingBarAnimation {
                        0% { transform: translateX(-60%); width: 35%; }
                        50% { transform: translateX(20%); width: 70%; }
                        100% { transform: translateX(120%); width: 35%; }
                      }
                      .loading-bar-animation {
                        animation: loadingBarAnimation 1.2s ease-in-out infinite;
                      }
                    `}</style>
                  </div>
                )}

                {/* Content */}
                <div className="max-lg:px-1 lg:px-7 pb-4 pt-[17px] sm:pb-4">
                  <article
                    className={[
                      // Typography: đẹp + dễ đọc mobile/desktop
                      "prose prose-slate max-w-none",
                      "prose-p:leading-relaxed prose-li:leading-relaxed",
                      "sm:prose-base lg:prose-lg",
                      // Links / images / code / tables: nhìn sạch và không vỡ layout
                      "prose-a:no-underline hover:prose-a:underline",
                      "prose-img:rounded-2xl prose-img:shadow-md",
                      "prose-hr:border-slate-200",
                      "prose-blockquote:border-slate-300 prose-blockquote:text-slate-700",
                      "prose-pre:overflow-x-auto prose-pre:rounded-2xl prose-pre:bg-slate-900 prose-pre:text-slate-50",
                      "prose-code:before:content-none prose-code:after:content-none",
                      "prose-table:block prose-table:overflow-x-auto",
                      // Lock the first rendered block to a stable top spacing.
                      "[&>*:first-child]:mt-0 [&>*:first-child]:pt-0",
                      "[&_article]:mt-0 [&_article]:pt-0",
                      "[&_article>*:first-child]:mt-0 [&_article>*:first-child]:pt-0",
                      // Prevent embedded bootstrap-like containers from shrinking the article.
                      "[&_.container]:w-full [&_.container]:max-w-none [&_.container]:mx-0 [&_.container]:px-0",
                      // Keep media full-width inside the article.
                      "[&_img]:block [&_img]:w-full [&_img]:max-w-none [&_img]:h-auto",
                      "[&_table]:w-full [&_table]:max-w-none",
                      "[&_iframe]:w-full [&_iframe]:max-w-full",
                      "[&_video]:w-full [&_video]:max-w-full",
                      // Anti-break
                      "break-words",
                    ].join(" ")}
                  >
                    {renderedArticle}
                  </article>

                  {/* Footer */}
                  <div className="mt-0 flex w-full flex-wrap items-center justify-between gap-2.5 border-t border-slate-200 pt-3">
                    <div className="inline-flex max-w-full items-center self-center rounded-xl border border-slate-200/80 bg-slate-50/70 px-3 py-1.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                      <div className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs leading-5 text-slate-500 sm:text-[13px]">
                        <span className="font-medium text-slate-700">By Tín Nguyễn Đăng</span>{" "}
                        <span className="text-slate-300">•</span>{" "}
                        <span>{labels[lang]?.writtenDate} {safeDate}</span>
                      </div>
                    </div>
                    <div className="ml-auto flex shrink-0 items-center self-center gap-2">
                      <button
                        type="button"
                        onClick={handleToggleLike}
                        className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border text-[20px] leading-none shadow-sm transition ${
                          isLiked
                            ? "border-slate-300 bg-slate-100 text-slate-900"
                            : "border-slate-300/90 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                        aria-label={isLiked ? "Unlike article" : "Like article"}
                        title={isLiked ? "Unlike" : "Like"}
                      >
                        {isLiked ? "♥" : "♡"}
                      </button>
                      <button
                        onClick={handleShareButtonClick}
                        className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 active:scale-[0.98]"
                      >
                        {labels[lang]?.share}
                      </button>
                    </div>
                  </div>

                </div>
              </div>

              {showHelpfulBox ? (
                <div className="mt-3 mb-4 rounded-2xl border border-slate-200/80 bg-white/85 p-3 shadow-lg ring-1 ring-black/5 backdrop-blur-md">
                  <div className="mx-4">
                    {helpfulVote ? (
                      <div className="text-sm font-medium text-slate-700">
                        {helpfulVote === "yes"
                          ? "Thank you for your feedback! We're glad you found this helpful."
                          : "Thank you for your feedback! We'll work on improving our content."}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-base font-semibold text-slate-800">
                          Was this content helpful to you?
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleHelpfulVote("yes")}
                            className="inline-flex h-9 min-w-[88px] items-center justify-center rounded-lg border border-slate-400/70 bg-white px-4 text-base font-medium text-blue-700 transition hover:bg-slate-50"
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleHelpfulVote("no")}
                            className="inline-flex h-9 min-w-[88px] items-center justify-center rounded-lg border border-slate-400/70 bg-white px-4 text-base font-medium text-blue-700 transition hover:bg-slate-50"
                          >
                            No
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Small note for mobile spacing */}
              <div className="h-0" />
            </div>
          </div>
        </>
      ) : (
        <div className={`${embedded ? "min-h-[40vh] px-0 py-0" : "min-h-[40vh] px-3 py-10"} bg-[radial-gradient(1200px_600px_at_50%_-150px,rgba(236,72,153,.12),transparent_60%),radial-gradient(900px_500px_at_10%_10%,rgba(59,130,246,.10),transparent_55%),radial-gradient(900px_500px_at_90%_20%,rgba(34,197,94,.08),transparent_55%)]`}>
          <div className={embedded ? "mx-0 max-w-none" : "mx-auto max-w-4xl"}>
            <div className="rounded-3xl border border-rose-200/70 bg-white/85 p-6 text-center shadow-xl ring-1 ring-black/5 backdrop-blur-md">
              <div className="text-lg font-bold text-rose-600">Oops…</div>
              <div className="mt-2 text-sm text-slate-600">{error}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Posts;
